import React, { useState, useEffect, ChangeEvent, KeyboardEvent, Dispatch, SetStateAction } from 'react';
import Button from './ui/Button';

interface TypeAheadProps {
  csvPath: string; // Path to your CSV file (e.g., "/my-file.csv")
  selectedItems: string[];
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
}

const TypeAhead: React.FC<TypeAheadProps> = ({ csvPath, selectedItems, setSelectedItems }) => {
  // Full list from CSV
  const [allOptions, setAllOptions] = useState<string[]>([]);
  
  // Current input text
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Filtered suggestions based on searchTerm
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  // Fetch CSV on mount
  useEffect(() => {
    const fetchCsv = async () => {
      try {
        const response = await fetch(csvPath);
        const csvText = await response.text();
        
        // Simple parse: split by newlines, trim, and remove empty lines
        const lines = csvText.split('\n');
        const rows = lines
          .map(line => line.trim())
          .filter(line => line.length > 0);

        setAllOptions(rows);
      } catch (error) {
        console.error('Error loading CSV:', error);
      }
    };

    fetchCsv();
  }, [csvPath]);

  // Update suggestions whenever the searchTerm changes
  useEffect(() => {
    if (!searchTerm) {
      setSuggestions([]);
    } else {
      const filtered = allOptions.filter(item =>
        item.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Limit to 10 suggestions
      setSuggestions(filtered.slice(0, 10));
    }
  }, [searchTerm, allOptions]);

  // Handle text input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle selection from suggestions
  const handleSelectSuggestion = (item: string) => {
    if (!selectedItems.includes(item)) {
      setSelectedItems(prev => [...prev, item]);
    }
    // Clear out the search box and suggestions
    setSearchTerm('');
    setSuggestions([]);
  };

  // Remove item from selected array
  const handleRemoveSelected = (item: string) => {
    setSelectedItems(prev => prev.filter(x => x !== item));
  };

  // Called when the user presses Enter or clicks "Add"
  // to add a custom term if it's not found in the suggestions.
  const handleAddCustomItem = () => {
    const newItem = searchTerm.trim();
    if (!newItem) return;

    // If it's already selected, do nothing
    if (!selectedItems.includes(newItem)) {
      setSelectedItems(prev => [...prev, newItem]);
    }

    // Optionally add to allOptions so it appears in future suggestions
    if (!allOptions.includes(newItem)) {
      setAllOptions(prev => [...prev, newItem]);
    }

    setSearchTerm('');
    setSuggestions([]);
  };

  // If user presses Enter with a value not in suggestions, add it
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission if inside a form
      // If there's an *exact* match in suggestions, you might want
      // to select that automatically. Otherwise, add as a custom item.
      // For simplicity, we're always just adding custom if user hits Enter:
      handleAddCustomItem();
    }
  };

  return (
    <div className="w-full">

     
      {/* The text input + "Add" button */}
      <div className="relative">
        <div className="flex flex-col gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Type to search..."
          />
          <Button 
            onClick={handleAddCustomItem} 
          >
            Add
          </Button>
        </div>

        {/* Suggestions dropdown */}
        {suggestions.length > 0 && (
          <ul
            className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {suggestions.map(item => (
              <li
                key={item}
                onClick={() => handleSelectSuggestion(item)}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer truncate"
              >
                {item}
              </li>
            ))}
          </ul>
        )}

      <div className="flex gap-2 mt-4 flex-col h-48 overflow-y-scroll ">
        {selectedItems.map(item => (
          <div
            key={item}
            className="px-3 py-1 bg-gray-100 rounded-md flex justify-between gap-2 max-w-full"
          >
            <span className="truncate">{item}</span>
            <button
              onClick={() => handleRemoveSelected(item)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Remove item"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      </div>
    </div>
  );
};

export default TypeAhead;
